import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PlusCircle } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { 
  getStatusBadgeStyle, 
  formatDate,
} from "../ClientUtils";
import { ClientSubscriptionDialog } from "../ClientSubscriptionDialog";
import { useTranslation } from "react-i18next";
import { useCurrency } from "@/contexts/CurrencyContext";
import { isSubscriptionExpired } from "@/components/subscriptions/SubscriptionFormSchema";
import { useIsMobile } from "@/hooks/use-mobile";
import { Client } from "@/types/types";
import { getStatusTextFromSubscription } from "@/utils/clientUtils";

interface ClientSubscriptionsTabProps {
  client: Client;
  paymentsBySubscription : Record<string, any[]>;
}

export function ClientSubscriptionsTab({ client , paymentsBySubscription}: ClientSubscriptionsTabProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { t } = useTranslation(["common", "clients", "subscriptions", "payments"]);
  const { formatCurrency } = useCurrency();
  const isMobile = useIsMobile();

  const formattedSubscriptions = (client.subscriptionHistory || []).map((sub) => {
    const isExpired = isSubscriptionExpired(sub.end_date);
    const status = isExpired && sub.status !== "Paused" ? "Expired" as const : sub.status;
  
    let numericPrice: number;
    if (typeof sub.price === "number") {
      numericPrice = sub.price;
    } else {
      numericPrice = 0;
    }
    const totalPaid =
    paymentsBySubscription[sub.id] && Array.isArray(paymentsBySubscription[sub.id])
        ? paymentsBySubscription[sub.id].reduce((sum, p) => sum + (p.amount || 0), 0)
        : 0;
  
    return {
      ...sub,
      status,
      numericPrice,
      totalPaid,
      paymentFrequencyDisplay: sub.payment_frequency
        ? t(`paymentFrequencies.${sub.payment_frequency}`, { ns: "subscriptions" })
        : "-",
      manuallyActivated: sub.is_manually_activated ? t("common:yes") : t("common:no"),
    };
  });
  

  return (
    <div className="w-full flex justify-center">
      <Card className="w-full max-w-screen-xl">
        <CardHeader className="flex flex-wrap justify-between items-center gap-4">
          <CardTitle className="text-lg">
            {t("clients:subscriptionHistory")}
          </CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsDialogOpen(true)}
            disabled={client.status === "Paused" || client.status === "Blocked"}
            className="whitespace-nowrap"
          >
            <PlusCircle className="h-4 w-4 mr-2" />
            {t("clients:newSubscription")}
          </Button>
        </CardHeader>

        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <div className={isMobile ? "max-h-[300px] overflow-y-auto" : ""}>
              <Table className="min-w-[900px]">
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("common:type")}</TableHead>
                    <TableHead>{t("common:start")}</TableHead>
                    <TableHead>{t("common:end")}</TableHead>
                    <TableHead>{t("subscriptions:sessions.total")}</TableHead>
                    <TableHead>{t("subscriptions:jokers.total")}</TableHead>
                    <TableHead>{t("common:price")}</TableHead>
                    <TableHead>{t("payments:amountReceived")}</TableHead>
                    <TableHead>{t("subscriptions:paymentFrequency")}</TableHead>
                    <TableHead>{t("subscriptions:manualActivation")}</TableHead>
                    <TableHead>{t("common:status")}</TableHead>
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {formattedSubscriptions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center py-4 text-muted-foreground">
                        {t("noSubscriptionsFound", { ns: "subscriptions" })}
                      </TableCell>
                    </TableRow>
                  ) : (
                    formattedSubscriptions.map((subscription) => (
                      <TableRow key={subscription.id}>
                        <TableCell className="font-medium">{subscription.type}</TableCell>
                        <TableCell>{formatDate(subscription.start_date)}</TableCell>
                        <TableCell>{formatDate(subscription.end_date)}</TableCell>
                        <TableCell>
                          {subscription.sessions_total !== undefined
                            ? `${subscription.sessions_remaining || 0}/${subscription.sessions_total}`
                            : "-"}
                        </TableCell>
                        <TableCell>
                          {subscription.jokers_total !== undefined
                            ? `${subscription.jokers_remaining || 0}/${subscription.jokers_total}`
                            : "-"}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {formatCurrency(subscription.numericPrice)}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {formatCurrency(subscription.totalPaid || 0)}
                        </TableCell>
                        <TableCell>{subscription.paymentFrequencyDisplay}</TableCell>
                        <TableCell>{subscription.manuallyActivated}</TableCell>
                        <TableCell>
                          <Badge
                            className={getStatusBadgeStyle(
                              getStatusTextFromSubscription(subscription) || "Active"
                            )}
                          >
                            {t(getStatusTextFromSubscription(subscription), {
                              ns: "subscriptions",
                            })}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </CardContent>
      </Card>

      <ClientSubscriptionDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        client={client}
        forceCreateNewSubscriptionOnOpen={true}
      />
    </div>
  );
}
